import type {
  GenerationBgData,
  GenerationKoma,
  GenerationSourceBreakdown,
} from '@ebalance/proto/bffuser/v1/balance_model_pb'
import type { GetGenerationDashboardResponse } from '@ebalance/proto/bffuser/v1/balance_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import {
  GEN_SOURCE_KEY_BY_TYPE,
  emptyGenSourceRecord,
} from '~/constants/generationSource'
import type { GenSourceKey } from '~/constants/generationSource'
import type { BgConfig } from '~/pages/BalanceGeneration/constants'
import type {
  BgSummary,
  HatsuKomaData,
} from '~/pages/BalanceGeneration/models/hatsuKoma'
import { summarizeBg } from '~/pages/BalanceGeneration/models/hatsuKoma'

export type BgApiData = {
  config: BgConfig
  komaData: HatsuKomaData[]
  summary: BgSummary
}

export type GenerationDashboardData = {
  bgs: BgApiData[]
  gcKoma: number
  currentKoma: number
}

/**
 * google.type.Decimal → number | null
 * undefined (= backend 未結線 / 欠損) は null を返す。
 */
function decimalToNumber(d: Decimal | undefined): number | null {
  if (d === undefined) return null
  const n = Number(d.value)
  return Number.isNaN(n) ? null : n
}

/**
 * source_breakdown (enum + value の repeated list、未特定の区分は要素自体が
 * 無い) を `HatsuKomaData` の flat record (9 区分すべてを持つ、無い区分は
 * null) へ変換する。
 *
 * `repeated GenerationSourceBreakdown` は proto 上 `unique` 相当の制約も
 * 件数上限も持たない（response 専用の message のため protovalidate
 * interceptor は inbound request しか検査せず、宣言しても発火しない —
 * balance_model.proto の `GenerationSourceBreakdown` コメント参照）。
 * 一意性・非 UNSPECIFIED を保証できるのは producer（backend の breakdown
 * 構築側、#2807）だけなので、ここでは**値を 1 kWh も落とさずに畳む**:
 *
 * - `GEN_SOURCE_KEY_BY_TYPE` で解決できない sourceType（UNSPECIFIED や将来の
 *   未知値）の値は**「その他」に加算する**。spec `doji-hatsu.md` の決定表
 *   「発電形式を特定できない実績の扱い (#7720)」が決定 A =「その他」に集約と
 *   定め、B (内訳から除外) を明示的に否決している。根拠は AC-BDH-010 /
 *   AC-BDH-020 NOTE「9区分の積み上げ合計が発電実績合計と一致する状態を保つ
 *   （内訳から除外して合計を欠けさせない）」。UNSPECIFIED は backend の bug
 *   ではなく**業務上ありうる値**（電源ユニットマスタ未登録・受電地点特定番号
 *   未入力のユニット由来）なので、未知 enum の silent
 *   fallback とは扱いが異なる
 * - 9 区分の外に系列は増やさない（conventions.md「揚水・蓄電池は9区分に
 *   加えない」と同じ原則）ので、受け皿は「その他」1 つに限る
 * - 同一 sourceType が複数回来た場合も加算する（先勝ち / 後勝ちのどちらで
 *   採っても採らなかった側の値が消え、上と同じ理由で合計が欠ける）
 *
 * どちらも契約違反の入力なので `console.warn` は残すが、**値は落とさない**
 * （警告は producer を直すための診断であって、表示を欠けさせる理由ではない）。
 */
function breakdownToRecord(
  items: GenerationSourceBreakdown[],
): Record<GenSourceKey, number | null> {
  // 9 key の網羅は `emptyGenSourceRecord` (= tsc が網羅を強制する静的な
  // SOURCE_META から作る) に保証させる。 `GENERATION_SOURCE_LIST` から seed を
  // 作ると、9 件性が `DEFAULT_STACK_ORDER` という別の配列リテラルに依存する
  // (詳細は generationSource.ts の `emptyGenSourceRecord` docstring)。
  const record = emptyGenSourceRecord<number | null>(null)
  /** 未提供 (null) と「実測 0」を潰さないよう、値が無い要素は加算しない。 */
  const add = (key: GenSourceKey, value: number | null): void => {
    if (value === null) return
    record[key] = (record[key] ?? 0) + value
  }
  const seen = new Set<GenSourceKey>()
  for (const item of items) {
    const key = GEN_SOURCE_KEY_BY_TYPE.get(item.sourceType)
    if (key === undefined) {
      // eslint-disable-next-line no-console -- 開発時に気づけるようにする (画面は落とさない)
      console.warn(
        `convertGenerationResponse: unresolved source_type (${String(item.sourceType)}) in breakdown — folding into "other"`,
      )
      add('other', decimalToNumber(item.value))
      continue
    }
    if (seen.has(key)) {
      // eslint-disable-next-line no-console -- 同上
      console.warn(
        `convertGenerationResponse: duplicate source_type "${key}" in breakdown — summing the values`,
      )
    }
    seen.add(key)
    add(key, decimalToNumber(item.value))
  }
  return record
}

function convertKoma(k: GenerationKoma): HatsuKomaData {
  return {
    time: k.time,
    // gen_plan は backend が plan_generation_value から解決する (#1999)。
    // 対象日に計画が存在しない BG は undefined → HatsuKomaData.genPlan は
    // number 必須なので 0 にフォールバックする。
    genPlan: decimalToNumber(k.genPlan) ?? 0,
    genActual: decimalToNumber(k.genActual),
    // 発電形式内訳 (HJKS 9 区分) は backend 未結線で常に空リスト → 全区分 null
    // (#1998/#2807)。deprecated な旧 flat field (thermal 等) は参照しない。
    ...breakdownToRecord(k.sourceBreakdown),
  }
}

function convertBgData(bg: GenerationBgData): BgApiData {
  // procurementType / type / capacity は BFF レスポンスに含まれないフィールド。
  // BgChart の描画に使われるのは name / code / procurementType / areaCode のみ。
  //   procurementType: '' — ヘッダに表示されるが空文字で問題なし
  //   type: 'mixed' — BgConfig の表示専用フィールドで chart 描画では不使用
  //   capacity: 0 — summarizeBg で avgUtilPct 計算に使われるが、capacity=0 の場合は 0 になる
  const config: BgConfig = {
    code: bg.bgCode,
    name: bg.bgName,
    procurementType: '',
    areaCode: bg.areaCode,
    type: 'mixed',
    capacity: 0,
  }
  const komaData = bg.komas.map(convertKoma)
  return {
    config,
    komaData,
    summary: summarizeBg(komaData, config.capacity),
  }
}

export function convertGenerationDashboardResponse(
  resp: GetGenerationDashboardResponse,
): GenerationDashboardData {
  return {
    bgs: resp.bgs.map(convertBgData),
    gcKoma: resp.gcKoma,
    currentKoma: resp.currentKoma,
  }
}
